import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Newspaper, Plus, Sparkles, Pencil, Trash2, Eye, EyeOff, ExternalLink, RefreshCw, ArrowLeft, Save, ImageIcon, Upload,
  Wand2, CalendarClock, Loader2, Clock, Search, Facebook, Share2, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { toast } from 'sonner';
import { adminGet, adminPost, adminPut, adminDelete, adminUpload, reportError, formatDate, fadeUp, BACKEND_URL } from './adminApi';
import { BlogMarkdown } from '../BlogMarkdown';

const EMPTY = { title: '', slug: '', excerpt: '', content_md: '', category: 'menage-entretien', tags: [], faq: [], status: 'draft', cover_url: null, cover_id: null, share_facebook: true };
const coverSrc = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
};
const wordCount = (md) => (md || '').replace(/[#*_>`[\]()|-]+/g, ' ').split(/\s+/).filter(Boolean).length;

/* ------------------------------------------------------------------ */
/* Auto-publication card                                               */
/* ------------------------------------------------------------------ */
function AutoStatusText({ loading, status }) {
  if (loading) return <Skeleton className="h-4 w-72 mt-1" />;
  if (!status) return <p className="text-xs text-destructive">Statut indisponible.</p>;
  return (
    <div className="text-xs text-muted-foreground space-y-0.5 mt-0.5" data-testid="blog-auto-status">
      <p>{status.schedule}. Prochaine catégorie : <strong className="text-foreground">{status.next_category_label}</strong>{status.next_run ? ` · prochain passage le ${formatDate(status.next_run, true)}` : ''}.</p>
      <p>{status.last_auto ? <>Dernier article automatique : <strong className="text-foreground">{status.last_auto.title}</strong> ({formatDate(status.last_auto.created_at)})</> : 'Aucun article automatique pour le moment.'}</p>
      {!status.enabled && <p className="text-destructive">Aucune clé IA configurée — la publication automatique est en pause.</p>}
    </div>
  );
}

function AutoPublishCard({ adminKey, onGenerated }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await adminGet('/api/admin/blog/auto-status', adminKey);
      setStatus(res.data);
      return res.data;
    } catch (err) {
      reportError('statut auto-publication blog', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  const runNow = async () => {
    setStarting(true);
    try {
      const res = await adminPost('/api/admin/blog/auto-run', adminKey);
      toast[res.data.started ? 'success' : 'info'](res.data.message);
      if (res.data.started) {
        const before = status?.published_total ?? 0;
        clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
          const s = await load();
          if (s && !s.running && s.published_total > before) {
            clearInterval(pollRef.current);
            toast.success('Nouvel article publié automatiquement');
            onGenerated?.();
          }
        }, 8000);
      }
    } catch (err) {
      reportError('génération auto blog', err, (m) => toast.error(m || 'Impossible de lancer la génération'));
    } finally {
      setStarting(false);
    }
  };

  return (
    <Card className="p-5" data-testid="blog-auto-card">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center shrink-0"><CalendarClock className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
          <div>
            <h3 className="font-['Space_Grotesk'] font-semibold">Publication automatique hebdomadaire</h3>
            <AutoStatusText loading={loading} status={status} />
          </div>
        </div>
        <Button size="sm" onClick={runNow} disabled={starting || status?.running || !status?.enabled} data-testid="blog-auto-run">
          {status?.running || starting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
          {status?.running ? 'Génération en cours…' : 'Générer et publier maintenant'}
        </Button>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* AI writer dialog                                                    */
/* ------------------------------------------------------------------ */
function AiWriterDialog({ open, onOpenChange, adminKey, categories, defaultCategory, onResult }) {
  const [topic, setTopic] = useState('');
  const [category, setCategory] = useState(defaultCategory || 'menage-entretien');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open && defaultCategory) setCategory(defaultCategory); }, [open, defaultCategory]);

  const generate = async () => {
    if (topic.trim().length < 4) { toast.error('Indiquez un sujet (4 caractères minimum)'); return; }
    setBusy(true);
    try {
      const res = await adminPost('/api/admin/blog/generate', adminKey, {}, { topic: topic.trim(), category, notes });
      onResult(res.data.article);
      onOpenChange(false);
      setTopic(''); setNotes('');
      toast.success('Article rédigé — relisez, ajustez puis publiez');
    } catch (err) {
      reportError('rédaction IA blog', err, (m) => toast.error(m || 'La rédaction IA a échoué'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg" data-testid="blog-ai-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Space_Grotesk'] flex items-center gap-2"><Wand2 className="h-5 w-5 text-[hsl(var(--primary))]" /> Rédiger avec l'IA</DialogTitle>
          <DialogDescription>L'IA rédige un article complet (1 000 à 1 400 mots, structure SEO, FAQ, liens internes). Vous relisez et publiez quand vous êtes satisfait.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ai-topic">Sujet de l'article</Label>
            <Input id="ai-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Ex : Comment entretenir une haie de thuyas en automne" maxLength={200} disabled={busy} data-testid="blog-ai-topic" />
          </div>
          <div className="space-y-1.5">
            <Label>Catégorie</Label>
            <Select value={category} onValueChange={setCategory} disabled={busy}>
              <SelectTrigger data-testid="blog-ai-category"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ai-notes">Consignes complémentaires <span className="text-muted-foreground font-normal">(optionnel)</span></Label>
            <Textarea id="ai-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={600} placeholder="Angle, public visé, points à aborder, mots-clés…" disabled={busy} data-testid="blog-ai-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={generate} disabled={busy} data-testid="blog-ai-submit">
            {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Rédaction en cours (30 à 90 s)…</> : <><Sparkles className="h-4 w-4 mr-1.5" /> Rédiger l'article</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Editor                                                              */
/* ------------------------------------------------------------------ */
function CoverControls({ adminKey, post, form, setForm }) {
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState('');
  const fileRef = useRef(null);
  const saved = Boolean(post?.id);

  const applyCover = (data) => setForm((f) => ({ ...f, cover_url: data.cover_url, cover_id: data.cover_id }));

  const generate = async () => {
    setBusy(true);
    try {
      const res = await adminPost(`/api/admin/blog/${post.id}/cover/generate`, adminKey, {}, { prompt, style: 'photo' });
      applyCover(res.data); toast.success('Image de couverture générée');
    } catch (err) {
      reportError('génération couverture', err, (m) => toast.error(m || 'Génération impossible'));
    } finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('image', file);
      const res = await adminUpload('post', `/api/admin/blog/${post.id}/cover/upload`, adminKey, fd);
      applyCover(res.data); toast.success('Image de couverture téléversée');
    } catch (err) {
      reportError('téléversement couverture', err, (m) => toast.error(m || 'Téléversement impossible'));
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const src = coverSrc(form.cover_url);
  return (
    <div className="space-y-3" data-testid="blog-cover-controls">
      <Label>Image de couverture</Label>
      <div className="rounded-xl border border-border overflow-hidden bg-[hsl(var(--brand-mint))]/40 aspect-[16/9] flex items-center justify-center relative">
        {src ? <img src={src} alt="Couverture" className="w-full h-full object-cover" data-testid="blog-cover-preview" /> : <ImageIcon className="h-10 w-10 text-[hsl(var(--primary))]/50" />}
        {busy && <div className="absolute inset-0 bg-card/70 backdrop-blur-sm flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>}
      </div>
      {!saved ? (
        <p className="text-xs text-muted-foreground">Enregistrez d'abord l'article (brouillon) pour ajouter une couverture.</p>
      ) : (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Description de l'image (optionnel, sinon déduite de l'article)" maxLength={400} disabled={busy} className="text-xs" data-testid="blog-cover-prompt" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={generate} disabled={busy} data-testid="blog-cover-generate"><Sparkles className="h-4 w-4 mr-1.5" /> Générer avec l'IA</Button>
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="blog-cover-upload-btn"><Upload className="h-4 w-4 mr-1.5" /> Téléverser</Button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="blog-cover-upload-input" />
            {form.cover_url && <Button size="sm" variant="ghost" onClick={() => setForm((f) => ({ ...f, cover_url: null, cover_id: null }))} disabled={busy}>Retirer</Button>}
          </div>
        </div>
      )}
    </div>
  );
}

function FaqEditor({ faq, onChange }) {
  const update = (i, key, value) => onChange(faq.map((f, idx) => (idx === i ? { ...f, [key]: value } : f)));
  return (
    <div className="space-y-3" data-testid="blog-faq-editor">
      <div className="flex items-center justify-between">
        <Label>FAQ <span className="text-muted-foreground font-normal">(rich snippet Google)</span></Label>
        <Button size="sm" variant="ghost" type="button" onClick={() => onChange([...faq, { question: '', reponse: '' }])} disabled={faq.length >= 8} data-testid="blog-faq-add"><Plus className="h-4 w-4 mr-1" /> Question</Button>
      </div>
      {faq.length === 0 && <p className="text-xs text-muted-foreground">Aucune question. Ajoutez 3 questions fréquentes pour enrichir le référencement.</p>}
      {faq.map((f, i) => (
        <div key={`faq-${i}`} className="rounded-lg border border-border p-3 space-y-2 bg-card">
          <div className="flex gap-2">
            <Input value={f.question} onChange={(e) => update(i, 'question', e.target.value)} placeholder="Question ?" maxLength={200} className="text-sm" />
            <Button size="icon" variant="ghost" type="button" onClick={() => onChange(faq.filter((_, idx) => idx !== i))} aria-label="Supprimer la question"><Trash2 className="h-4 w-4" /></Button>
          </div>
          <Textarea value={f.reponse} onChange={(e) => update(i, 'reponse', e.target.value)} placeholder="Réponse en 2-3 phrases" rows={2} maxLength={1200} className="text-sm" />
        </div>
      ))}
    </div>
  );
}

function FacebookSharePanel({ adminKey, post, form, setForm, onShared }) {
  const [busy, setBusy] = useState(false);
  const share = post?.fb_share;
  const shareNow = async () => {
    setBusy(true);
    try {
      const res = await adminPost(`/api/admin/blog/${post.id}/share-facebook`, adminKey);
      onShared(res.data.fb_share);
      toast.success('Article partagé sur la page Facebook');
    } catch (err) {
      reportError('partage Facebook', err, (m) => toast.error(m || 'Partage impossible'));
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="blog-facebook-panel">
      <div className="flex items-center justify-between gap-2">
        <Label className="flex items-center gap-1.5"><Facebook className="h-4 w-4 text-[hsl(var(--primary))]" /> Facebook</Label>
        {share?.status === 'shared' && <Badge className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] text-[10px]" data-testid="blog-fb-status"><CheckCircle2 className="h-3 w-3 mr-1" /> Partagé</Badge>}
        {share?.status === 'failed' && <Badge variant="outline" className="text-[10px] border-destructive/50 text-destructive" data-testid="blog-fb-status"><AlertTriangle className="h-3 w-3 mr-1" /> Échec</Badge>}
        {share?.status === 'skipped' && <Badge variant="secondary" className="text-[10px]" data-testid="blog-fb-status">Non partagé</Badge>}
      </div>
      <label className="flex items-center justify-between gap-2 text-sm cursor-pointer">
        <span>Partager automatiquement à la publication</span>
        <Switch checked={form.share_facebook !== false} onCheckedChange={(v) => setForm((f) => ({ ...f, share_facebook: v }))} data-testid="blog-share-facebook-switch" />
      </label>
      {share?.status === 'shared' && (
        <div className="text-[11px] text-muted-foreground space-y-1">
          <p>Partagé le {formatDate(share.shared_at, true)}{share.url && <> · <a href={share.url} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-0.5">voir la publication <ExternalLink className="h-3 w-3" /></a></>}</p>
          {share.message && <p className="line-clamp-3 italic">« {share.message} »</p>}
        </div>
      )}
      {share?.error && share.status !== 'shared' && <p className="text-[11px] text-destructive flex items-start gap-1" data-testid="blog-fb-error"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {share.error}</p>}
      {post?.id && post.status === 'published' ? (
        <Button size="sm" variant="outline" className="w-full" onClick={shareNow} disabled={busy} data-testid="blog-share-facebook-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Share2 className="h-4 w-4 mr-1.5" />} {share?.status === 'shared' ? 'Partager à nouveau' : 'Partager sur Facebook maintenant'}
        </Button>
      ) : (
        <p className="text-[11px] text-muted-foreground">Le partage manuel est disponible une fois l'article publié.</p>
      )}
    </div>
  );
}

function PostEditor({ adminKey, categories, initial, onBack, onSaved, onOpenAi }) {
  const [post, setPost] = useState(initial?.id ? initial : null);
  const [form, setForm] = useState({ ...EMPTY, ...initial, tags: initial?.tags || [], faq: initial?.faq || [] });
  const [tagsInput, setTagsInput] = useState((initial?.tags || []).join(', '));
  const [saving, setSaving] = useState(false);
  const [loadingFull, setLoadingFull] = useState(Boolean(initial?.id && initial?.content_md === undefined));
  const dirty = useRef(false);

  // Load full content when editing an existing post from the list
  useEffect(() => {
    if (!initial?.id || initial.content_md !== undefined) return;
    adminGet(`/api/admin/blog/${initial.id}`, adminKey)
      .then((res) => { setPost(res.data.item); setForm({ ...EMPTY, ...res.data.item }); setTagsInput((res.data.item.tags || []).join(', ')); })
      .catch((err) => reportError('chargement article', err, () => toast.error('Impossible de charger l\'article')))
      .finally(() => setLoadingFull(false));
  }, [initial, adminKey]);

  // Apply AI-generated content coming from the parent
  useEffect(() => {
    if (initial?.__ai) {
      setForm((f) => ({ ...f, ...initial, tags: initial.tags || [], faq: initial.faq || [] }));
      setTagsInput((initial.tags || []).join(', '));
      dirty.current = true;
    }
  }, [initial]);

  const set = (key) => (value) => { dirty.current = true; setForm((f) => ({ ...f, [key]: value })); };
  const words = useMemo(() => wordCount(form.content_md), [form.content_md]);

  const save = async (statusOverride) => {
    const status = statusOverride || form.status;
    if (form.title.trim().length < 5) { toast.error('Le titre doit faire au moins 5 caractères'); return; }
    if (form.content_md.trim().length < 20) { toast.error('Le contenu est trop court'); return; }
    const faq = form.faq.filter((f) => f.question.trim() && f.reponse.trim()).map((f) => ({ question: f.question.trim(), reponse: f.reponse.trim() }));
    const payload = {
      title: form.title.trim(), slug: form.slug?.trim() || undefined, excerpt: form.excerpt.trim(), content_md: form.content_md, category: form.category,
      tags: tagsInput.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 12), faq, status,
      cover_url: form.cover_url || undefined, cover_id: form.cover_id || undefined,
      origin: form.origin || (initial?.__ai ? 'ai' : undefined), ai_meta: initial?.__ai ? initial._meta : undefined,
      share_facebook: form.share_facebook !== false,
    };
    setSaving(true);
    try {
      const res = post?.id
        ? await adminPut(`/api/admin/blog/${post.id}`, adminKey, {}, payload)
        : await adminPost('/api/admin/blog', adminKey, {}, payload);
      const item = res.data.item;
      setPost(item); setForm((f) => ({ ...f, ...item, faq: item.faq || [], tags: item.tags || [] })); setTagsInput((item.tags || []).join(', '));
      dirty.current = false;
      toast.success(status === 'published' ? 'Article publié' : 'Brouillon enregistré');
      onSaved(item);
    } catch (err) {
      reportError('enregistrement article', err, (m) => toast.error(m || 'Enregistrement impossible'));
    } finally {
      setSaving(false);
    }
  };

  if (loadingFull) return <div className="space-y-3" data-testid="blog-editor-loading"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-96 w-full" /></div>;

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp} className="space-y-5" data-testid="blog-editor">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="self-start" data-testid="blog-editor-back"><ArrowLeft className="h-4 w-4 mr-1" /> Retour à la liste</Button>
        <div className="flex flex-wrap items-center gap-2">
          {post?.slug && post.status === 'published' && (
            <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm"><ExternalLink className="h-4 w-4 mr-1.5" /> Voir en ligne</Button></a>
          )}
          <Button variant="outline" size="sm" onClick={onOpenAi} data-testid="blog-editor-ai"><Wand2 className="h-4 w-4 mr-1.5" /> Rédiger avec l'IA</Button>
          <Button variant="outline" size="sm" onClick={() => save('draft')} disabled={saving} data-testid="blog-save-draft"><Save className="h-4 w-4 mr-1.5" /> Enregistrer le brouillon</Button>
          <Button size="sm" onClick={() => save('published')} disabled={saving} data-testid="blog-publish">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />} {post?.status === 'published' ? 'Mettre à jour' : 'Publier'}</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        {/* Main column */}
        <div className="space-y-4">
          <Card className="p-5 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="post-title">Titre</Label>
              <Input id="post-title" value={form.title} onChange={(e) => set('title')(e.target.value)} placeholder="Titre de l'article (55 à 70 caractères idéalement)" maxLength={140} className="text-base font-medium" data-testid="blog-title-input" />
              <p className="text-[11px] text-muted-foreground text-right">{form.title.length} / 70</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="post-excerpt">Extrait / meta description</Label>
              <Textarea id="post-excerpt" value={form.excerpt} onChange={(e) => set('excerpt')(e.target.value)} rows={2} maxLength={320} placeholder="Résumé affiché dans Google et sur les cartes (130 à 155 caractères)" data-testid="blog-excerpt-input" />
              <p className={`text-[11px] text-right ${form.excerpt.length > 160 ? 'text-destructive' : 'text-muted-foreground'}`}>{form.excerpt.length} / 155</p>
            </div>
          </Card>

          <Card className="p-5">
            <Tabs defaultValue="edit">
              <div className="flex items-center justify-between mb-3">
                <TabsList>
                  <TabsTrigger value="edit" data-testid="blog-tab-edit"><Pencil className="h-3.5 w-3.5 mr-1.5" /> Rédaction</TabsTrigger>
                  <TabsTrigger value="preview" data-testid="blog-tab-preview"><Eye className="h-3.5 w-3.5 mr-1.5" /> Aperçu</TabsTrigger>
                </TabsList>
                <span className="text-xs text-muted-foreground" data-testid="blog-word-count">{words} mots · ~{Math.max(1, Math.round(words / 200))} min</span>
              </div>
              <TabsContent value="edit">
                <Textarea value={form.content_md} onChange={(e) => set('content_md')(e.target.value)} rows={26} className="font-mono text-[13px] leading-relaxed" placeholder={"## Introduction\n\nVotre texte en **Markdown** : titres avec ##, listes avec -, liens [texte](/devis)…"} data-testid="blog-content-input" />
                <p className="text-[11px] text-muted-foreground mt-2">Mise en forme Markdown : <code>## Titre</code>, <code>### Sous-titre</code>, <code>**gras**</code>, <code>- liste</code>, <code>[lien](/credit-impot)</code>, tableaux et citations <code>&gt;</code>.</p>
              </TabsContent>
              <TabsContent value="preview">
                <div className="rounded-xl border border-border p-5 sm:p-8 bg-card min-h-[300px]" data-testid="blog-preview">
                  {form.content_md.trim() ? <BlogMarkdown content={form.content_md} /> : <p className="text-sm text-muted-foreground">Rien à prévisualiser pour le moment.</p>}
                </div>
              </TabsContent>
            </Tabs>
          </Card>

          <Card className="p-5"><FaqEditor faq={form.faq} onChange={set('faq')} /></Card>
        </div>

        {/* Side column */}
        <div className="space-y-4">
          <Card className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <Label>Statut</Label>
              <Badge variant={form.status === 'published' ? 'default' : 'secondary'} data-testid="blog-status-badge">{form.status === 'published' ? 'Publié' : 'Brouillon'}</Badge>
            </div>
            <div className="space-y-1.5">
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={set('category')}>
                <SelectTrigger data-testid="blog-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="post-slug">Adresse (slug)</Label>
              <div className="flex items-center gap-1 text-xs text-muted-foreground"><span>/blog/</span><Input id="post-slug" value={form.slug || ''} onChange={(e) => set('slug')(e.target.value)} placeholder="généré depuis le titre" className="text-xs h-8" data-testid="blog-slug-input" /></div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="post-tags">Mots-clés <span className="text-muted-foreground font-normal">(séparés par des virgules)</span></Label>
              <Input id="post-tags" value={tagsInput} onChange={(e) => { dirty.current = true; setTagsInput(e.target.value); }} placeholder="ménage, automne, astuces" data-testid="blog-tags-input" />
            </div>
            {post?.published_at && <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> Publié le {formatDate(post.published_at, true)}</p>}
            {form.ai_meta?.text_model && <p className="text-[11px] text-muted-foreground">Rédigé avec {form.ai_meta.text_model}</p>}
          </Card>
          <Card className="p-5"><CoverControls adminKey={adminKey} post={post} form={form} setForm={setForm} /></Card>
          <Card className="p-5"><FacebookSharePanel adminKey={adminKey} post={post} form={form} setForm={setForm} onShared={(fbShare) => setPost((p) => ({ ...p, fb_share: fbShare }))} /></Card>
        </div>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Tab                                                                 */
/* ------------------------------------------------------------------ */
export function AdminBlogTab({ adminKey }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ published: 0, draft: 0 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null | {} (new) | post
  const [aiOpen, setAiOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [aiSeed, setAiSeed] = useState(0);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const res = await adminGet('/api/admin/blog', adminKey);
      setItems(res.data.items || []); setCounts(res.data.counts || {}); setCategories(res.data.categories || []);
    } catch (err) {
      setError(true); reportError('liste blog', err, () => toast.error('Impossible de charger les articles'));
    } finally { setLoading(false); }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (post) => {
    try {
      const full = (await adminGet(`/api/admin/blog/${post.id}`, adminKey)).data.item;
      const status = full.status === 'published' ? 'draft' : 'published';
      await adminPut(`/api/admin/blog/${post.id}`, adminKey, {}, {
        title: full.title, slug: full.slug, excerpt: full.excerpt, content_md: full.content_md, category: full.category, tags: full.tags || [], faq: full.faq || [], status,
        cover_url: full.cover_url || undefined, cover_id: full.cover_id || undefined, share_facebook: full.share_facebook !== false,
      });
      toast.success(status === 'published' ? 'Article publié' : 'Article dépublié (brouillon)');
      load();
    } catch (err) {
      reportError('changement de statut', err, (m) => toast.error(m || 'Action impossible'));
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await adminDelete(`/api/admin/blog/${toDelete.id}`, adminKey);
      toast.success('Article supprimé'); setToDelete(null); load();
    } catch (err) {
      reportError('suppression article', err, (m) => toast.error(m || 'Suppression impossible'));
    }
  };

  const onAiResult = (article) => {
    // Open (or update) the editor with the AI draft
    setEditing((prev) => ({ ...(prev?.id ? { id: prev.id, status: prev.status, cover_url: prev.cover_url, cover_id: prev.cover_id } : {}), ...article, status: prev?.status || 'draft', __ai: true, __seed: aiSeed + 1 }));
    setAiSeed((s) => s + 1);
  };

  const visible = items.filter((p) => (filter === 'all' || p.status === filter) && (!search.trim() || p.title.toLowerCase().includes(search.trim().toLowerCase())));

  const renderList = () => {
    if (loading && items.length === 0) {
      return (
        <div className="space-y-2" data-testid="blog-list-loading">{[0, 1, 2].map((i) => <Skeleton key={`bl-skel-${i}`} className="h-20 rounded-xl" />)}</div>
      );
    }
    if (error) {
      return (
        <div className="text-center py-8" data-testid="blog-list-error"><p className="text-sm text-muted-foreground mb-3">Les articles n'ont pas pu être chargés.</p><Button size="sm" variant="outline" onClick={load}>Réessayer</Button></div>
      );
    }
    if (visible.length === 0) {
      return (
        <div className="text-center py-10" data-testid="blog-list-empty">
          <Newspaper className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground mb-4">{items.length === 0 ? 'Aucun article pour le moment. Rédigez le premier ou laissez l\'IA s\'en charger.' : 'Aucun article ne correspond à ce filtre.'}</p>
          {items.length === 0 && <Button size="sm" onClick={() => { setEditing({ ...EMPTY }); setAiOpen(true); }}><Wand2 className="h-4 w-4 mr-1.5" /> Rédiger avec l'IA</Button>}
        </div>
      );
    }
    return (
      <ul className="space-y-2" data-testid="blog-list">
        {visible.map((p) => (
          <li key={p.id} className="flex items-center gap-3 rounded-xl border border-border p-3 bg-card hover:border-[hsl(var(--primary))]/40 transition-colors" data-testid="blog-list-item">
            <div className="w-20 h-14 rounded-lg overflow-hidden bg-[hsl(var(--brand-mint))]/50 shrink-0 hidden sm:flex items-center justify-center">
              {p.cover_url ? <img src={coverSrc(p.cover_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="h-5 w-5 text-[hsl(var(--primary))]/50" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                <Badge variant={p.status === 'published' ? 'default' : 'secondary'} className="text-[10px]" data-testid="blog-item-status">{p.status === 'published' ? 'Publié' : 'Brouillon'}</Badge>
                <Badge variant="outline" className="text-[10px] font-normal">{p.category_label}</Badge>
                {p.origin !== 'manual' && <Badge variant="outline" className="text-[10px] font-normal border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))]"><Sparkles className="h-2.5 w-2.5 mr-1" />{p.origin === 'auto' ? 'Auto' : 'IA'}</Badge>}
                {p.fb_share?.status === 'shared' && <Badge variant="outline" className="text-[10px] font-normal" data-testid="blog-item-fb"><Facebook className="h-2.5 w-2.5 mr-1" /> Facebook</Badge>}
                {p.fb_share?.status === 'failed' && <Badge variant="outline" className="text-[10px] font-normal border-destructive/50 text-destructive" title={p.fb_share.error} data-testid="blog-item-fb"><Facebook className="h-2.5 w-2.5 mr-1" /> Échec FB</Badge>}
              </div>
              <p className="text-sm font-medium truncate" data-testid="blog-item-title">{p.title}</p>
              <p className="text-[11px] text-muted-foreground truncate">/blog/{p.slug} · {p.word_count} mots · {p.status === 'published' ? `publié le ${formatDate(p.published_at)}` : `modifié le ${formatDate(p.updated_at)}`}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {p.status === 'published' && <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" aria-label="Voir en ligne"><Button size="icon" variant="ghost" className="h-8 w-8"><ExternalLink className="h-4 w-4" /></Button></a>}
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => toggleStatus(p)} aria-label={p.status === 'published' ? 'Dépublier' : 'Publier'} data-testid="blog-item-toggle">{p.status === 'published' ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(p)} aria-label="Modifier" data-testid="blog-item-edit"><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setToDelete(p)} aria-label="Supprimer" data-testid="blog-item-delete"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  if (editing !== null) {
    return (
      <>
        <PostEditor key={editing.id || `new-${editing.__seed || 0}`} adminKey={adminKey} categories={categories} initial={editing}
          onBack={() => { setEditing(null); load(); }} onSaved={(item) => setEditing((e) => ({ ...e, id: item.id, status: item.status, slug: item.slug }))} onOpenAi={() => setAiOpen(true)} />
        <AiWriterDialog open={aiOpen} onOpenChange={setAiOpen} adminKey={adminKey} categories={categories} defaultCategory={editing.category} onResult={onAiResult} />
      </>
    );
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp} className="space-y-5" data-testid="admin-blog-tab">
      <AutoPublishCard adminKey={adminKey} onGenerated={load} />

      <Card className="p-5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center"><Newspaper className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
            <div>
              <h3 className="font-['Space_Grotesk'] font-semibold">Articles du blog</h3>
              <p className="text-xs text-muted-foreground" data-testid="blog-counts">{counts.published || 0} publié(s) · {counts.draft || 0} brouillon(s)</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => { setEditing({ ...EMPTY }); setAiOpen(true); }} data-testid="blog-new-ai"><Wand2 className="h-4 w-4 mr-1.5" /> Rédiger avec l'IA</Button>
            <Button size="sm" onClick={() => setEditing({ ...EMPTY })} data-testid="blog-new"><Plus className="h-4 w-4 mr-1.5" /> Nouvel article</Button>
            <Button size="sm" variant="ghost" onClick={load} disabled={loading} aria-label="Actualiser" data-testid="blog-refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="flex gap-1.5">
            {[['all', 'Tous'], ['published', 'Publiés'], ['draft', 'Brouillons']].map(([id, label]) => (
              <Button key={id} size="sm" variant={filter === id ? 'default' : 'outline'} onClick={() => setFilter(id)} data-testid={`blog-filter-${id}`}>{label}</Button>
            ))}
          </div>
          <div className="relative sm:ml-auto sm:w-64">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un titre…" className="pl-8 h-9" data-testid="blog-admin-search" />
          </div>
        </div>

        {renderList()}
      </Card>

      <AiWriterDialog open={aiOpen} onOpenChange={(o) => { setAiOpen(o); if (!o && editing && !editing.title && !editing.id) { /* keep empty editor open */ } }} adminKey={adminKey} categories={categories} onResult={onAiResult} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="blog-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet article ?</AlertDialogTitle>
            <AlertDialogDescription>« {toDelete?.title} » sera définitivement supprimé. Si l'article est indexé par Google, la page renverra une erreur 404.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="blog-delete-confirm">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
